import {
    getProductByBarcode,
    getProductById,
    getProductsByName,
    getAllProducts,
    getProductsCount
} from './productsDB';

// Универсальная функция поиска товаров
export const searchProducts = async (query, searchType = 'auto', limit = 50) => {
    try {
        if (!query || query.trim().length === 0) {
            return [];
        }

        const trimmedQuery = query.trim();

        switch (searchType) {
            case 'barcode':
                const productByBarcode = await getProductByBarcode(trimmedQuery);
                return productByBarcode ? [productByBarcode] : [];

            case 'id':
                const productById = await getProductById(trimmedQuery);
                return productById ? [productById] : [];

            case 'name':
                return await getProductsByName(trimmedQuery, limit);

            case 'auto':
            default:
                // Автоматическое определение типа поиска
                return await autoSearchProducts(trimmedQuery, limit);
        }
    } catch (error) {
        console.error('Ошибка поиска товаров:', error);
        return [];
    }
};

// Автоматический поиск с определением типа запроса
const autoSearchProducts = async (query, limit = 50) => {
    const results = [];

    // 1. Если запрос похож на ID (только цифры)
    if (/^\d+$/.test(query)) {
        const productById = await getProductById(query);
        if (productById) {
            results.push(productById);
        }
    }

    // 2. Если запрос похож на штрихкод (длинная строка цифр)
    if (query.length >= 8 && /^\d+$/.test(query)) {
        const productByBarcode = await getProductByBarcode(query);
        if (productByBarcode && !results.find(p => p.id === productByBarcode.id)) {
            results.push(productByBarcode);
        }
    }

    // 3. Поиск по названию (если еще не нашли или нужно больше результатов)
    if (results.length === 0 || (results.length < limit && query.length >= 2)) {
        const productsByName = await getProductsByName(query, limit - results.length);
        productsByName.forEach(product => {
            if (!results.find(p => p.id === product.id)) {
                results.push(product);
            }
        });
    }

    return results.slice(0, limit);
};

// Быстрый поиск товара по штрихкоду (основная функция для POS)
export const findProductByBarcode = async (barcode) => {
    try {
        if (!barcode || barcode.trim().length === 0) {
            return null;
        }

        const product = await getProductByBarcode(barcode.trim());
        return product;
    } catch (error) {
        console.error('Ошибка поиска товара по штрихкоду:', error);
        return null;
    }
};

// Поиск товаров с дополнительной информацией
export const searchProductsWithInfo = async (query, searchType = 'auto', limit = 50) => {
    try {
        const startTime = Date.now();
        const products = await searchProducts(query, searchType, limit);
        const searchTime = Date.now() - startTime;

        return {
            products,
            searchTime,
            query,
            searchType,
            totalFound: products.length,
            hasMore: products.length === limit
        };
    } catch (error) {
        console.error('Ошибка расширенного поиска товаров:', error);
        return {
            products: [],
            searchTime: 0,
            query,
            searchType,
            totalFound: 0,
            hasMore: false,
            error: error.message
        };
    }
};

// Получение статистики товаров
export const getProductsStats = async () => {
    try {
        const totalCount = await getProductsCount();

        // Получаем несколько товаров для анализа
        const sampleProducts = await getAllProducts(0, 100);

        let totalValue = 0;
        let inStockCount = 0;
        let outOfStockCount = 0;

        sampleProducts.forEach(product => {
            if (product.price && product.stock) {
                totalValue += product.price * product.stock;
            }

            if (product.stock > 0) {
                inStockCount++;
            } else {
                outOfStockCount++;
            }
        });

        return {
            totalProducts: totalCount,
            inStock: inStockCount,
            outOfStock: outOfStockCount,
            sampleSize: sampleProducts.length,
            estimatedValue: totalValue
        };
    } catch (error) {
        console.error('Ошибка получения статистики товаров:', error);
        return {
            totalProducts: 0,
            inStock: 0,
            outOfStock: 0,
            sampleSize: 0,
            estimatedValue: 0,
            error: error.message
        };
    }
};

// Валидация товара
export const validateProduct = (product) => {
    if (!product || typeof product !== 'object') {
        return { isValid: false, errors: ['Товар не найден'] };
    }

    const errors = [];

    if (!product.id) {
        errors.push('Отсутствует ID товара');
    }

    if (!product.name || product.name.trim().length === 0) {
        errors.push('Отсутствует название товара');
    }

    if (!product.price || product.price <= 0) {
        errors.push('Некорректная цена товара');
    }

    if (!product.barcodes || !Array.isArray(product.barcodes) || product.barcodes.length === 0) {
        errors.push('Отсутствуют штрихкоды');
    }

    if (typeof product.stock !== 'number' || product.stock < 0) {
        errors.push('Некорректный остаток товара');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

// Форматирование цены товара
export const formatProductPrice = (price, currency = '₸') => {
    if (!price || typeof price !== 'number') {
        return '0 ' + currency;
    }

    return new Intl.NumberFormat('kk-KZ', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(price) + ' ' + currency;
};

// Получение основного штрихкода товара
export const getPrimaryBarcode = (product) => {
    if (!product || !product.barcodes || !Array.isArray(product.barcodes)) {
        return null;
    }

    return product.barcodes[0] || null;
};

// Проверка наличия товара в наличии
export const isProductInStock = (product) => {
    if (!product || typeof product.stock !== 'number') {
        return false;
    }

    return product.stock > 0;
};

// Получение информации о наличии товара
export const getStockInfo = (product) => {
    if (!product) {
        return { status: 'unknown', message: 'Товар не найден' };
    }

    const stock = product.stock || 0;

    if (stock > 10) {
        return { status: 'available', message: `В наличии: ${stock} ${product.unit || 'шт'}` };
    } else if (stock > 0) {
        return { status: 'low', message: `Мало в наличии: ${stock} ${product.unit || 'шт'}` };
    } else {
        return { status: 'out', message: 'Нет в наличии' };
    }
};

// Поиск похожих товаров по названию
export const findSimilarProducts = async (productName, excludeId = null, limit = 10) => {
    try {
        if (!productName || productName.length < 3) {
            return [];
        }

        // Извлекаем ключевые слова из названия
        const keywords = productName
            .toLowerCase()
            .split(/\s+/)
            .filter(word => word.length > 2)
            .slice(0, 3); // Берем первые 3 ключевых слова

        const similarProducts = [];

        for (const keyword of keywords) {
            const products = await getProductsByName(keyword, limit);
            products.forEach(product => {
                if (product.id !== excludeId && !similarProducts.find(p => p.id === product.id)) {
                    similarProducts.push(product);
                }
            });

            if (similarProducts.length >= limit) {
                break;
            }
        }

        return similarProducts.slice(0, limit);
    } catch (error) {
        console.error('Ошибка поиска похожих товаров:', error);
        return [];
    }
};